import { useCallback, useEffect, useRef, useState } from 'react';
import type { ObjectDiscussionChannel, ObjectDiscussionMessageType } from '@econtractor/types';
import {
    addObjectDiscussionMessage,
    getObjectDiscussion,
    markObjectDiscussionRead,
} from 'api/apiObjectDiscussion';
import { latestDiscussionMessageId, mergeLatestDiscussionMessages } from './discussionMessages';

export const OBJECT_DISCUSSION_POLL_MS = 30000;

const viewIsCurrent = (
    activeKey: string,
    activeSession: number,
    key: string,
    session: number,
): boolean => activeKey === key && activeSession === session;

const useObjectDiscussion = (
    objectId: number,
    channel: ObjectDiscussionChannel,
    onSeen?: () => void,
) => {
    const [messages, setMessages] = useState<ObjectDiscussionMessageType[]>([]);
    const [canWrite, setCanWrite] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const sendingRef = useRef(false);
    const onSeenRef = useRef(onSeen);
    const seenRef = useRef(false);
    const loadSeqRef = useRef(0);
    const markedIdRef = useRef(0);
    const sessionRef = useRef(0);
    const activeKeyRef = useRef(`${channel}:${objectId}`);
    const requestKey = `${channel}:${objectId}`;
    if (activeKeyRef.current !== requestKey) {
        activeKeyRef.current = requestKey;
        loadSeqRef.current = 0;
        markedIdRef.current = 0;
        seenRef.current = false;
        sendingRef.current = false;
        sessionRef.current += 1;
    }
    onSeenRef.current = onSeen;

    const ackRead = useCallback(async (key: string, session: number, messageId: number) => {
        const current = () => viewIsCurrent(activeKeyRef.current, sessionRef.current, key, session);
        if (!current()) return;
        if (!messageId || messageId <= markedIdRef.current) {
            onSeenRef.current?.();
            return;
        }
        const marked = await markObjectDiscussionRead(channel, objectId, messageId);
        if (!current()) return;
        if (!marked) return;
        markedIdRef.current = Math.max(markedIdRef.current, messageId);
        onSeenRef.current?.();
    }, [channel, objectId]);

    const load = useCallback(async (quiet: boolean) => {
        if (!objectId) return;
        const key = `${channel}:${objectId}`;
        const session = sessionRef.current;
        const seq = loadSeqRef.current + 1;
        loadSeqRef.current = seq;
        if (!quiet) setRefreshing(true);

        const data = await getObjectDiscussion(channel, objectId);
        const current = () => viewIsCurrent(activeKeyRef.current, sessionRef.current, key, session);
        if (!current() || seq !== loadSeqRef.current) return;

        if (!data) {
            setRefreshing(false);
            setLoaded(true);
            if (!seenRef.current) setLoadFailed(true);
            return;
        }

        seenRef.current = true;
        setLoadFailed(false);
        setMessages((prev) => (
            current() ? mergeLatestDiscussionMessages(prev, data.messages) : prev
        ));
        setCanWrite(data.canWrite);
        setLoaded(true);
        setRefreshing(false);

        await ackRead(key, session, latestDiscussionMessageId(data.messages));
    }, [ackRead, channel, objectId]);

    useEffect(() => {
        seenRef.current = false;
        markedIdRef.current = 0;
        sendingRef.current = false;
        setLoaded(false);
        setLoadFailed(false);
        setFailed(false);
        setSending(false);
        setMessages([]);
        void load(true);
        const timer = window.setInterval(() => {
            void load(true);
        }, OBJECT_DISCUSSION_POLL_MS);
        return () => {
            sessionRef.current += 1;
            window.clearInterval(timer);
        };
    }, [channel, objectId, load]);

    const refresh = () => {
        void load(false);
    };

    const send = async (text: string): Promise<boolean> => {
        if (!objectId || sendingRef.current) return false;
        const key = `${channel}:${objectId}`;
        const session = sessionRef.current;
        sendingRef.current = true;
        setSending(true);
        setFailed(false);
        const message = await addObjectDiscussionMessage(channel, objectId, text);
        const current = () => viewIsCurrent(activeKeyRef.current, sessionRef.current, key, session);
        if (!current()) {
            sendingRef.current = false;
            return false;
        }
        sendingRef.current = false;
        setSending(false);
        if (!message) {
            setFailed(true);
            return false;
        }
        setMessages((prev) => (
            current() ? mergeLatestDiscussionMessages(prev, [message]) : prev
        ));
        seenRef.current = true;
        setLoadFailed(false);
        await ackRead(key, session, message.ID);
        return true;
    };

    return {
        messages,
        canWrite,
        loaded,
        loadFailed,
        sending,
        failed,
        refreshing,
        send,
        refresh,
    };
};

export default useObjectDiscussion;
